import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState } from '@cca/ui';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface AuditEvent {
  readonly id: string;
  readonly actorType: string;
  readonly actorName: string | null;
  readonly agentRunId: string | null;
  readonly action: string;
  readonly targetType: string | null;
  readonly targetId: string | null;
  readonly approvalId: string | null;
  readonly outcome: string;
  readonly errorCode: string | null;
  readonly detail: Record<string, unknown>;
  readonly correlationId: string | null;
  readonly occurredAt: string;
}

const OUTCOME_TONE: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  success: 'success',
  failure: 'danger',
  denied: 'danger',
  skipped: 'neutral',
};

const ACTOR_TONE: Record<string, 'info' | 'success' | 'warning' | 'neutral'> = {
  user: 'info',
  agent: 'warning',
  automation: 'warning',
  system: 'neutral',
  provider: 'neutral',
};

function DetailRow({ event }: { readonly event: AuditEvent }) {
  const [open, setOpen] = useState(false);
  const hasDetail = Object.keys(event.detail).length > 0;

  return (
    <>
      <tr className="border-b border-slate-100 align-top dark:border-slate-800">
        <td className="px-3 py-2 text-xs whitespace-nowrap text-slate-500 dark:text-slate-400">
          {new Date(event.occurredAt).toLocaleString()}
        </td>
        <td className="px-3 py-2">
          <Badge tone={ACTOR_TONE[event.actorType] ?? 'neutral'}>{event.actorType}</Badge>
          {event.actorName ? (
            <span className="ml-1.5 text-xs text-slate-600 dark:text-slate-400">
              {event.actorName}
            </span>
          ) : null}
        </td>
        <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">{event.action}</td>
        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
          {event.targetType ? (
            <>
              {event.targetType}
              {event.targetId ? (
                <span className="ml-1 font-mono text-[11px]">{event.targetId.slice(0, 8)}</span>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </td>
        <td className="px-3 py-2">
          <Badge tone={OUTCOME_TONE[event.outcome] ?? 'neutral'}>{event.outcome}</Badge>
          {event.errorCode ? (
            <span className="ml-1.5 text-xs text-rose-700 dark:text-rose-400">
              {event.errorCode}
            </span>
          ) : null}
        </td>
        <td className="px-3 py-2 text-right">
          {hasDetail ? (
            <Button size="sm" variant="ghost" onClick={() => setOpen((value) => !value)}>
              {open ? 'Hide' : 'Detail'}
            </Button>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-slate-100 dark:border-slate-800">
          <td colSpan={6} className="px-3 pb-3">
            <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 text-[11px] text-slate-800 dark:bg-slate-900 dark:text-slate-200">
              {JSON.stringify(event.detail, null, 2)}
            </pre>
            {event.correlationId ? (
              <p className="mt-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                correlation {event.correlationId}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AuditPage() {
  const accessToken = useAccessToken();
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [appliedCorrelation, setAppliedCorrelation] = useState('');

  const actions = useQuery({
    queryKey: ['audit-actions'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<{ actions: string[] }>('/api/audit/actions', { accessToken }),
  });

  const events = useQuery({
    queryKey: ['audit', action, actorType, outcome, appliedCorrelation],
    enabled: Boolean(accessToken),
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (action) params.set('action', action);
      if (actorType) params.set('actorType', actorType);
      if (outcome) params.set('outcome', outcome);
      if (appliedCorrelation) params.set('correlationId', appliedCorrelation);
      return apiRequest<{ events: AuditEvent[]; nextBefore: string | null }>(
        `/api/audit?${params.toString()}`,
        { accessToken },
      );
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Audit log</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Every agent run, tool denial, approval decision and send. Append-only — the database
          refuses updates and deletes on this table, and there is no route here that could write to
          it.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block text-slate-700 dark:text-slate-300">Action</span>
          <select
            value={action}
            onChange={(changeEvent) => setAction(changeEvent.target.value)}
            className="h-9 max-w-[16rem] rounded-md border border-slate-300 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="">Any action</option>
            {(actions.data?.actions ?? []).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-slate-700 dark:text-slate-300">Actor</span>
          <select
            value={actorType}
            onChange={(changeEvent) => setActorType(changeEvent.target.value)}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="">Anyone</option>
            <option value="user">User</option>
            <option value="agent">Agent</option>
            <option value="automation">Automation</option>
            <option value="system">System</option>
            <option value="provider">Provider</option>
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-slate-700 dark:text-slate-300">Outcome</span>
          <select
            value={outcome}
            onChange={(changeEvent) => setOutcome(changeEvent.target.value)}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="">Any outcome</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="denied">Denied</option>
            <option value="skipped">Skipped</option>
          </select>
        </label>

        <form
          className="flex items-end gap-2"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();
            setAppliedCorrelation(correlationId.trim());
          }}
        >
          <label className="text-sm">
            <span className="mb-1 block text-slate-700 dark:text-slate-300">Correlation id</span>
            <Input
              value={correlationId}
              onChange={(changeEvent) => setCorrelationId(changeEvent.target.value)}
              placeholder="Trace one request end to end"
              className="w-64 font-mono text-xs"
            />
          </label>
          <Button type="submit" size="sm" variant="outline">
            Trace
          </Button>
        </form>
      </div>

      <Card className="overflow-hidden p-0">
        {events.isPending ? <LoadingState label="Loading audit events" /> : null}

        {events.isError ? (
          <ErrorState
            description={
              events.error instanceof ApiError
                ? events.error.message
                : 'Could not load the audit log.'
            }
            onRetry={() => void events.refetch()}
          />
        ) : null}

        {events.data && events.data.events.length === 0 ? (
          <EmptyState
            title="Nothing recorded yet"
            description="Audit events appear as soon as the agent runs, an approval is decided, or a message is sent."
          />
        ) : null}

        {events.data && events.data.events.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">
                    When
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Actor
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Action
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Target
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Outcome
                  </th>
                  <th scope="col" className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {events.data.events.map((auditEvent) => (
                  <DetailRow key={auditEvent.id} event={auditEvent} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {events.data?.nextBefore ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Showing the most recent 100 events. Narrow the filters to see further back.
        </p>
      ) : null}
    </div>
  );
}

export const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: () => (
    <RequireSession>
      <AuditPage />
    </RequireSession>
  ),
});
